import React from "react"
import EditSidebar from "../../components/Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "../../components/Ui/EditSidebar/EditSidebarHeader"
import PrimaryButton from "../../components/Ui/Buttons/PrimaryButton"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import Input from "../../components/Ui/Form/Input"
import { useMutation } from "@apollo/client"
import CREATE_OR_UPDATE_INVENTORY from "../../graphql/mutations/inventory/CreateOrUpdateInventory"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { useAppDispatch } from "lib/store"
import { InputInventory } from "lib/types/generated/graphql-types"
import { Common } from "lib/types"
import SingleSelect from "../../components/Ui/Form/SingleSelect"
import { getTypeOptions } from "./helpers"
import { TypeWrapper } from "./Inventory.styled"

type Props = {
  refetch: () => void
}

export const CreateInventory = ({ refetch }: Props) => {
  interface FullInputInventory extends InputInventory {
    id: string
  }

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<FullInputInventory>({ mode: "onBlur" })

  const dispatch = useAppDispatch()

  const [addInventory, { loading: createLoading }] = useMutation(CREATE_OR_UPDATE_INVENTORY, {
    onCompleted: (data) => {
      dispatch(
        alertActions.actions.createAlert({
          message: `Inventory ${data?.createOrUpdateInventory?.name} successfully created`,
          type: "success"
        })
      )
      reset({
        id: "",
        name: "",
        description: "",
        address: {
          city: "",
          country: "",
          postalCode: "",
          region: "",
          streetAddress: ""
        },
        type: ""
      })
      refetch()
    },
    onError: () => {
      dispatch(
        alertActions.actions.createAlert({
          type: "error"
        })
      )
    }
  })

  const updateType = (type: Common.Option | null) => {
    setValue("type", type?.value || "")
  }

  const onSubmit: SubmitHandler<FullInputInventory> = (data) => {
    addInventory({
      variables: { ...data, id: data.id.replaceAll(" ", "") }
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <EditSidebar>
        <EditSidebarHeader title="Create Inventory">
          <PrimaryButton type="submit" handleClick={handleSubmit(onSubmit)} loading={createLoading}>
            Save
          </PrimaryButton>
        </EditSidebarHeader>
        <TypeWrapper>
        <Controller
          name="type"
          render={({ field }) => (
            <SingleSelect
              {...field}
              options={getTypeOptions()}
              placeholder="Type"
              label="Type"
              setValue={updateType}
              defaultValue={getTypeOptions()[0]}
            />
          )}
          control={control}
        />
        </TypeWrapper>
        <Controller
          name="id"
          render={({ field }) => (
            <Input
              {...field}
              label="Identifier *"
              type="text"
              placeholder="Identifier *"
              errors={errors}
            />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
        />
        <Controller
          name="name"
          render={({ field }) => (
            <Input {...field} label="Name *" type="text" placeholder="Name *" errors={errors} />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
        />
        <Controller
          name="description"
          render={({ field }) => (
            <Input
              {...field}
              label="Description *"
              type="text"
              placeholder="Description *"
              errors={errors}
            />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
        />
        <Controller
          name="address.city"
          render={({ field }) => (
            <Input {...field} type="text" label="City" placeholder="City" errors={errors} />
          )}
          control={control}
          defaultValue={""}
        />
        <Controller
          name="address.country"
          render={({ field }) => (
            <Input {...field} label="Country" type="text" placeholder="Country" errors={errors} />
          )}
          control={control}
          defaultValue={""}
        />
        <Controller
          name="address.postalCode"
          render={({ field }) => (
            <Input
              {...field}
              label="Postal code"
              type="text"
              placeholder="Postal code"
              errors={errors}
            />
          )}
          control={control}
          defaultValue={""}
        />
        <Controller
          name="address.region"
          render={({ field }) => (
            <Input {...field} label="Region" type="text" placeholder="Region" errors={errors} />
          )}
          control={control}
          defaultValue={""}
        />
        <Controller
          name="address.streetAddress"
          render={({ field }) => (
            <Input
              {...field}
              type="text"
              label="Street address"
              placeholder="Street Address"
              errors={errors}
            />
          )}
          control={control}
          defaultValue={""}
        />
      </EditSidebar>
    </form>
  )
}
